import { useEffect, useId, useMemo } from 'react';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { clsx } from 'clsx';
import useAnchoredDropdown from 'hooks/useAnchoredDropdown';

import UnitLinkChips from './components/UnitLinkChips';
import UnitLinkPicker from './components/UnitLinkPicker';

import type IUnitLinkField from './interfaces/IUnitLinkField';

import styles from './scss/UnitLinkField.module.scss';

function UnitLinkField({
  label,
  items,
  options,
  onToggle,
  onRemove,
  isEditDisabled = false,
  onBlockedEdit,
  onPickerOpen,
  onPickerClose,
  error,
  id,
  boundarySelector,
  className,
}: IUnitLinkField) {
  // Scoped so two fields on one page cannot collide: useScrollToError matches ids by substring.
  const generatedId = useId();
  const pickerId = `unit-link-picker-${id || generatedId}`;

  const {
    triggerRef, dropdownRef, isOpen, position, searchTerm, setSearchTerm, toggle, reset,
  } = useAnchoredDropdown({ boundarySelector, onClose: onPickerClose });

  // Discard rather than commit: editing was revoked, so pending choices are not the
  // user's to save.
  useEffect(() => {
    if (isEditDisabled) {
      reset();
    }
  }, [isEditDisabled, reset]);

  const selectedKeys = useMemo(
    () => new Set(items.map((item) => item.itemKey)),
    [items],
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return options;
    }

    return options.filter((option) => option.name.toLowerCase().includes(normalizedSearchTerm));
  }, [options, searchTerm]);

  const handleTriggerClick = () => {
    if (isEditDisabled) {
      onBlockedEdit?.();
      return;
    }
    if (!isOpen) {
      onPickerOpen?.();
    }
    toggle();
  };

  const handleRemove = (itemKey: string) => {
    if (isEditDisabled) {
      onBlockedEdit?.();
      return;
    }
    onRemove(itemKey);
  };

  return (
    <div className={clsx(styles.field, className)} id={id}>
      <div className={styles.header}>
        <span className={styles.label}>{label}</span>
        <button
          type="button"
          className={clsx(styles.addButton, {
            [styles.addButtonBlocked]: isEditDisabled,
          })}
          ref={triggerRef}
          onClick={handleTriggerClick}
          aria-label={label}
          aria-disabled={isEditDisabled}
          aria-expanded={isOpen}
          aria-controls={pickerId}
        >
          <PlusIcon />
        </button>
      </div>
      <UnitLinkChips
        items={items}
        onRemove={handleRemove}
        isRemoveDisabled={isEditDisabled}
      />
      {error ? <span className={styles.error}>{error}</span> : null}
      <UnitLinkPicker
        id={pickerId}
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        position={position}
        searchTerm={searchTerm}
        onSearchTermChange={(event) => setSearchTerm(event.target.value)}
        options={filteredOptions}
        selectedKeys={selectedKeys}
        onOptionToggle={onToggle}
      />
    </div>
  );
}

export default UnitLinkField;
